<script setup lang="ts">
// import { useCurrentUser } from "@/api/current-user";
import {
	useArchiveDist,
	useDeleteDistLogo,
	useDist,
	useUpdateDist,
	useUpdateDistLogo,
} from "@/api/distributor-query";
import { useDistPublicPages } from "@/api/public-page-query";
import { useDistUsers } from "@/api/user-query";
import AppAvatar from "@/components/common/AppAvatar.vue";
import SettingsFooter from "@/components/common/SettingsFooter.vue";
import CreatePublicPageDialog from "@/components/dialogs/CreatePublicPageDialog.vue";
import CreateUserDialog from "@/components/dialogs/CreateUserDialog.vue";
import StationsSettings from "@/components/distributor/StationsSettings.vue";
import FileSelectHelper from "@/components/helpers/FileSelectHelper.vue";
import PermissionGuard from "@/components/permission/PermissionGuard.vue";
import PublicPagesTable from "@/components/public-pages/PublicPagesTable.vue";
import UsersTable from "@/components/users/UsersTable.vue";
import { wheneverOnce } from "@/composable/whenever-once";
import { __SOFTDB_ID__ } from "@/constants";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { useScopeStore } from "@/stores/scope";
import { mdiAccountGroup, mdiCogOutline, mdiDelete } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { Distributor, UpdateDistributor } from "@soft-db/monitoring-api-client";
import {
	Button,
	confirm,
	Divider,
	FormField,
	Icon,
	InputGroup,
	LinkButton,
	TextInput,
} from "@soft-db/ui";
import { storeToRefs } from "pinia";

const { t } = useI18n();

const route = useRoute();
const distId = computed(() => route.params.id as string);

const { overwriteDistId } = storeToRefs(useScopeStore());
const isHost = computed(() => distId.value === __SOFTDB_ID__);
const canShowStationsSection = computed(() => !isHost.value && overwriteDistId.value != null);

// General Settings
const snackbar = useSnackbarStore();
const updateDistLogo = useUpdateDistLogo();
const deleteDistLogo = useDeleteDistLogo();

async function updateLogo(file: File) {
	await updateDistLogo
		.mutateAsync({ id: distId.value, image: file })
		.then(snackbar.openOnSaved)
		.catch(snackbar.openOnError);
}
async function deleteLogo() {
	await deleteDistLogo
		.mutateAsync({ dist: distId.value })
		.then(snackbar.openOnSaved)
		.catch(snackbar.openOnError);
}

const { useField, handleSubmit } = useForm<UpdateDistributor>();
const name = useField("name", { isRequired: true });

// Users
const { data: users } = useDistUsers(distId);

const userDialog = ref(false);
function openCreateUserDialog() {
	userDialog.value = true;
}
const router = useRouter();
function goToUser(userId: string) {
	router.push({
		name: "distributor-id-users-userid",
		params: { id: distId.value, userid: userId },
	});
}

const { publicPages } = useDistPublicPages(distId);

const publicPageDialog = ref(false);
function openCreatePublicPageDialog() {
	publicPageDialog.value = true;
}

function goToPublicPage(pageId: string) {
	router.push({
		name: "distributor-id-public-pages-pageid",
		params: { id: distId.value, pageid: pageId },
	});
}

// Save
function syncDist(dist: Distributor) {
	name.data.value = dist.name;
}

const { data: dist } = useDist(distId);

wheneverOnce(
	dist,
	(dist) => {
		syncDist(dist);
	},
	{ immediate: true }
);

const updateDistMutation = useUpdateDist();

const hasChanges = computed(() => {
	if (!dist.value) return false;

	return name.data.value !== dist.value.name;
});

function undoChanges() {
	if (!dist.value) return;

	syncDist(dist.value);
}

const saveChanges = handleSubmit(async (data) => {
	if (!dist.value) return;

	const updatedDist = await updateDistMutation.mutateAsync({
		id: distId.value,
		data: { name: data.name, timezone: dist.value.timezone },
	});

	syncDist(updatedDist);
	snackbar.openOnSaved();
});

// const { currentUser } = useCurrentUser();
const canArchive = computed(() => {
	// It cannot be done right now in management
	return false;
	// if (
	// 	(currentUser.value?.scope.type === "distributor" &&
	// 		currentUser.value.scope.id !== distId.value) ||
	// 	distId.value !== "2"
	// )
	// 	return true;
	// else return false;
});

const { resetContext } = useScopeStore();
const archival = useArchiveDist();
async function archive() {
	if (await confirm.yesNo(t("distributor.archiveMessage"))) {
		await archival.mutateAsync(distId.value);
		resetContext();
	}
}
</script>

<template>
	<ConfigSection :title="t('object.distributor')" :description="t('distributor.description')">
		<FileSelectHelper
			class="flex size-48 items-center justify-center rounded-full"
			:disabled="isHost"
			:showDelete="!!dist?.imageUrl"
			@update="updateLogo"
			@delete="deleteLogo"
		>
			<AppAvatar class="size-full" outlined :src="dist?.imageUrl" />
		</FileSelectHelper>
		<FormField class="w-full" :label="t('distributor.name')" :errors="name.errors.value">
			<InputGroup class="w-full">
				<Icon :d="mdiAccountGroup" />
				<TextInput v-model="name.data.value" :disabled="isHost" />
			</InputGroup>
		</FormField>
	</ConfigSection>
	<Divider />
	<ConfigSection :title="t('object.user', 2)" :description="t('distributor.userDescription')">
		<UsersTable
			:users="users || []"
			:settingLocation="
				(user) => ({ name: 'distributor-id-users-userid', params: { id: distId, userid: user.id } })
			"
		>
			<template #action>
				<Button variant="outline" @click="openCreateUserDialog">
					{{ t("actions.createUser") }}
				</Button>
			</template>
		</UsersTable>
	</ConfigSection>
	<Divider />
	<ConfigSection
		:title="t('space.publicPages', 2)"
		:description="t('distributor.publicPagesDescription')"
	>
		<PublicPagesTable :pages="publicPages || []">
			<template #tableAction>
				<Button variant="outline" @click="openCreatePublicPageDialog">
					{{ t("actions.createPublicPage") }}
				</Button>
			</template>
			<template #rowAction="{ page }">
				<LinkButton
					variant="soft"
					size="sm"
					:to="{
						name: 'distributor-id-public-pages-pageid',
						params: { id: distId, pageid: page.id },
					}"
				>
					<Icon :d="mdiCogOutline" />
				</LinkButton>
			</template>
		</PublicPagesTable>
	</ConfigSection>

	<template v-if="canShowStationsSection">
		<Divider />
		<StationsSettings :id="distId" />
	</template>

	<SettingsFooter class="mt-4" :hasChanges="hasChanges" @save="saveChanges" @undo="undoChanges">
		<template #prepend>
			<PermissionGuard v-if="canArchive" permission="*.*">
				<Button color="error" size="xs" variant="ghost" @click="archive">
					<Icon :d="mdiDelete" />
					<span>{{ t("distributor.archive") }}</span>
				</Button>
			</PermissionGuard>
		</template>
	</SettingsFooter>

	<CreateUserDialog
		v-model:dialog="userDialog"
		:scope="{ id: distId, type: 'distributor' }"
		@create="goToUser"
	/>
	<CreatePublicPageDialog
		v-model:dialog="publicPageDialog"
		:scope="{ id: distId, type: 'distributor' }"
		@create="goToPublicPage"
	/>
</template>
